import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface StcBinGrpDto {
    id: number;
    companyNo: string;
    binGrpNo: number;
    description1: string;
    commonAvailable: boolean;
    externalSystemID?: number;
    updatedBy?: string;
    lastUpdate?: string;
    createdBy?: string;
    created?: string;
}

export interface CreateStcBinGrpRequest {
    companyNo: string;
    binGrpNo: number;
    description1: string;
    commonAvailable: boolean;
    externalSystemID?: number;
}

export interface UpdateStcBinGrpRequest {
    binGrpNo?: number;
    description1: string;
    commonAvailable: boolean;
    externalSystemID?: number;
}

export interface BulkUpdateStcBinGrpRequest {
    companyNo: string;
    binGrpNo: number;
    description1: string;
    commonAvailable: boolean;
    externalSystemID?: number;
}

export interface BulkDeleteResult {
    deletedCount: number;
    requestedCount: number;
    notFoundIds: number[];
}

class StcBinGrpService {
    private baseUrl = `${API_BASE_URL}/stc-bin-groups`;

    async getByCompanyAndBinGrpNo(companyNo: string, binGrpNo: number): Promise<StcBinGrpDto | null> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${binGrpNo}`);
            if (!response.ok) {
                if (response.status === 404) {
                    return null;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error: any) {
            if (error.message?.includes('404')) {
                return null;
            }
            throw error;
        }
    }

    async getByCompanyNo(companyNo: string): Promise<StcBinGrpDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getCurrent(): Promise<StcBinGrpDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getCommonAvailable(companyNo: string): Promise<StcBinGrpDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}/common-available`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getByExternalSystemId(companyNo: string, externalSystemId: number): Promise<StcBinGrpDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}/external-system/${externalSystemId}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getAll(): Promise<StcBinGrpDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async create(request: CreateStcBinGrpRequest): Promise<StcBinGrpDto> {
        const response = await AuthService.authenticatedPost(`${this.baseUrl}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    async update(companyNo: string, binGrpNo: number, request: UpdateStcBinGrpRequest): Promise<StcBinGrpDto> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/${companyNo}/${binGrpNo}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    async bulkUpdate(requests: BulkUpdateStcBinGrpRequest[]): Promise<StcBinGrpDto[]> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/bulk`, requests);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    async delete(companyNo: string, binGrpNo: number): Promise<void> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${companyNo}/${binGrpNo}`);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
    }

    async bulkDelete(ids: number[]): Promise<BulkDeleteResult> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/bulk`, ids);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    async exists(companyNo: string, binGrpNo: number): Promise<boolean> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${binGrpNo}`);
            return response.ok;
        } catch (error: any) {
            return false;
        }
    }

    async search(companyNo: string, searchTerm: string): Promise<StcBinGrpDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/search/${companyNo}?searchTerm=${encodeURIComponent(searchTerm)}`);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }
}

export const stcBinGrpService = new StcBinGrpService();
